import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert, Box, IconButton, Paper, Slide, Snackbar, TextField, Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import WhatshotIcon from '@mui/icons-material/Whatshot';
import ScheduleIcon from '@mui/icons-material/Schedule';
import TimelapseIcon from '@mui/icons-material/Timelapse';
import LockIcon from '@mui/icons-material/Lock';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { useCloudModalAutoClose } from '../../hooks/useCloudModalAutoClose';
import { useOrientation } from '../../hooks/useOrientation';
import { SHC_BOTTOM_NAV_CLEARANCE } from '../../constants/bottomNav';
import { useOverlayBackButton } from '../../hooks/useOverlayBackButton';
import { EvohomeDevice, evohomeControl, evohomeRefreshDevice } from '../../services/evohomeApi';
import { mapaEvohome } from '../../services/evohomeState';
import { eventBus } from '../../services/EventBus';
import { requirePinIfNeeded } from '../../services/pinGate';
import EvohomeTempChart from './EvohomeTempChart';
import {
  EVOHOME_CMD_COOLDOWN_MS,
  EVOHOME_SETPOINT_DEBOUNCE_MS,
} from './evohomeCmdGuard';
import { formatInServerTz } from '../../utils/serverTime';
import { isHeatingOff } from './evohomeModeUtils';
import { getDemoRestrictionSnackbarProps } from '../../constants/demoRestrictions';

const ACCENT = '#2391FF';
const HEAT = '#FF8A65';
const SURFACE = 'rgba(255,255,255,0.04)';
const SURFACE_BORDER = 'rgba(255,255,255,0.08)';
const PRESS_TRANSITION = 'transform 140ms cubic-bezier(0.2, 0.9, 0.3, 1), background-color 160ms ease, border-color 160ms ease, box-shadow 180ms ease, color 160ms ease, opacity 90ms ease';

// TCC rate-limita y manda mails "cambio zonal fallido" si polleamos/comandamos de más.
const SETPOINT_DEBOUNCE_MS = EVOHOME_SETPOINT_DEBOUNCE_MS;
// 1 min: solo con modal abierto; /refresh cache (REFRESH_MIN_INTERVAL) evita martillar TCC.
const MODAL_SYNC_INTERVAL_MS = 60000;
const MODAL_SYNC_RETRY_MS = 90000;
const MODAL_SYNC_PAUSE_AFTER_CONTROL_MS = 45000;

const DURATION_OPTIONS_MIN = [30, 60, 120, 180, 240, 360, 480];

type SetpointModeUi = 'follow_schedule' | 'temporary_override' | 'permanent_override' | 'clear_override';

function normalizeMode(mode: string | null | undefined): SetpointModeUi | null {
  const m = String(mode || '').toLowerCase().replace(/-/g, '');
  if (!m) return null;
  if (m.includes('follow') || m === 'followschedule') return 'follow_schedule';
  if (m.includes('temporary') || m === 'temporaryoverride') return 'temporary_override';
  if (m.includes('permanent') || m === 'permanentoverride') return 'permanent_override';
  return null;
}

interface Props {
  open: boolean;
  device: EvohomeDevice | null;
  onClose: () => void;
  onUpdated?: (d: EvohomeDevice) => void;
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <Typography sx={{
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
      color: 'rgba(255,255,255,0.45)',
      mb: 0.85,
    }}>
      {children}
    </Typography>
  );
}

function StatusChip({ color, label }: { color: string; label: string }) {
  return (
    <Box sx={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 0.55,
      px: 0.9,
      py: 0.3,
      borderRadius: 999,
      bgcolor: `${color}18`,
      border: `1px solid ${color}55`,
      maxWidth: 118,
      flexShrink: 0,
    }}>
      <Box sx={{
        width: 6,
        height: 6,
        borderRadius: '50%',
        bgcolor: color,
        boxShadow: `0 0 8px ${color}`,
        flexShrink: 0,
        animation: 'evohomePulse 2.2s ease-in-out infinite',
        '@keyframes evohomePulse': {
          '0%, 100%': { opacity: 1 },
          '50%': { opacity: 0.45 },
        },
      }} />
      <Typography sx={{
        fontSize: 10,
        fontWeight: 600,
        color,
        letterSpacing: 0.15,
        lineHeight: 1.1,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}>
        {label}
      </Typography>
    </Box>
  );
}

function ChoiceChip({
  selected,
  disabled,
  onClick,
  color = ACCENT,
  children,
  minHeight = 40,
}: {
  selected: boolean;
  disabled?: boolean;
  onClick: () => void;
  color?: string;
  children: React.ReactNode;
  minHeight?: number;
}) {
  return (
    <Box
      component="button"
      type="button"
      disabled={disabled}
      onClick={onClick}
      sx={{
        appearance: 'none',
        border: selected ? `1px solid ${color}` : `1px solid ${SURFACE_BORDER}`,
        bgcolor: selected ? `${color}22` : SURFACE,
        color: selected ? '#fff' : 'rgba(255,255,255,0.72)',
        borderRadius: '12px',
        minHeight,
        px: 1,
        py: 0.75,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 0.35,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.4 : 1,
        transition: PRESS_TRANSITION,
        boxShadow: selected ? `0 6px 18px ${color}28, inset 0 0 0 1px ${color}33` : 'none',
        WebkitTapHighlightColor: 'transparent',
        '&:hover:not(:disabled)': {
          borderColor: selected ? color : 'rgba(255,255,255,0.18)',
          bgcolor: selected ? `${color}2e` : 'rgba(255,255,255,0.07)',
        },
        '&:active:not(:disabled)': {
          transform: 'scale(0.94)',
        },
      }}
    >
      {children}
    </Box>
  );
}

function RoundControl({
  disabled,
  onClick,
  ariaLabel,
  size = 48,
  children,
}: {
  disabled?: boolean;
  onClick: () => void;
  ariaLabel: string;
  size?: number;
  children: React.ReactNode;
}) {
  return (
    <IconButton
      aria-label={ariaLabel}
      disabled={disabled}
      onClick={onClick}
      sx={{
        width: size,
        height: size,
        color: '#fff',
        bgcolor: SURFACE,
        border: `1px solid ${SURFACE_BORDER}`,
        transition: PRESS_TRANSITION,
        WebkitTapHighlightColor: 'transparent',
        '&:hover:not(.Mui-disabled)': {
          bgcolor: 'rgba(255,255,255,0.08)',
          borderColor: ACCENT,
        },
        '&:active:not(.Mui-disabled)': {
          transform: 'scale(0.9)',
        },
        '&.Mui-disabled': {
          color: '#666',
          borderColor: '#2a2a2a',
          bgcolor: '#121212',
        },
      }}
    >
      {children}
    </IconButton>
  );
}

const MODE_META: Record<SetpointModeUi, { color: string; icon: React.ReactNode }> = {
  follow_schedule: { color: '#90A4AE', icon: <ScheduleIcon sx={{ fontSize: 18 }} /> },
  temporary_override: { color: '#FFB74D', icon: <TimelapseIcon sx={{ fontSize: 18 }} /> },
  permanent_override: { color: HEAT, icon: <LockIcon sx={{ fontSize: 18 }} /> },
  clear_override: { color: '#81C784', icon: <RestartAltIcon sx={{ fontSize: 18 }} /> },
};

const EvohomeDeviceModal: React.FC<Props> = ({ open, device, onClose, onUpdated }) => {
  const { t } = useTranslation();
  const isHorizontal = useOrientation();
  const [configBusy, setConfigBusy] = useState(false);
  const [ambientBusy, setAmbientBusy] = useState(false);
  const [localSetpoint, setLocalSetpoint] = useState<number | null>(null);
  const [editingSetpoint, setEditingSetpoint] = useState(false);
  const [setpointDraft, setSetpointDraft] = useState('');
  const [localMode, setLocalMode] = useState<SetpointModeUi | null>(null);
  const [durationMin, setDurationMin] = useState(60);
  const [error, setError] = useState<string | null>(null);
  const [cooldownUntil, setCooldownUntil] = useState(0);
  const [nowMs, setNowMs] = useState(() => Date.now());
  const modeCooldownTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [permissionSnackbar, setPermissionSnackbar] = useState<{ open: boolean; message: string }>({
    open: false,
    message: '',
  });
  const [isClosing, setIsClosing] = useState(false);
  const [mapTick, setMapTick] = useState(0);
  const setpointInputRef = useRef<HTMLInputElement | null>(null);
  const swipeStartRef = useRef<{ x: number; y: number } | null>(null);
  const setpointSeqRef = useRef(0);
  const setpointDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingSetpointRef = useRef<number | null>(null);
  const deviceIdRef = useRef<number | null>(null);
  const syncPausedUntilRef = useRef(0);
  const onUpdatedRef = useRef(onUpdated);
  onUpdatedRef.current = onUpdated;

  useEffect(() => {
    if (!open) return undefined;
    return eventBus.on('evohome:updated', () => setMapTick((n) => n + 1));
  }, [open]);

  useEffect(() => {
    if (cooldownUntil <= Date.now()) return undefined;
    const id = window.setInterval(() => setNowMs(Date.now()), 200);
    return () => window.clearInterval(id);
  }, [cooldownUntil]);

  useEffect(() => () => {
    if (modeCooldownTimerRef.current) clearTimeout(modeCooldownTimerRef.current);
  }, []);

  const live = device
    ? ((mapTick >= 0 && mapaEvohome[device.id]) || device)
    : null;
  deviceIdRef.current = live?.id ?? null;

  const pauseModalSync = useCallback(() => {
    syncPausedUntilRef.current = Date.now() + MODAL_SYNC_PAUSE_AFTER_CONTROL_MS;
  }, []);

  const minSp = live?.min_setpoint ?? 5;
  const maxSp = live?.max_setpoint ?? 35;

  const flushSetpoint = useCallback(async (next: number) => {
    const id = deviceIdRef.current;
    if (id == null) return;
    pauseModalSync();
    const seq = ++setpointSeqRef.current;
    try {
      // Desde Programa (± temp) → TemporaryOverride (comportamiento app Honeywell).
      // PermanentOverride solo si el usuario ya eligió Permanente.
      const mode = localMode ?? normalizeMode(mapaEvohome[id]?.setpoint_mode);
      const value = mode === 'permanent_override'
        ? next
        : { temperature: next, duration_minutes: durationMin };
      const res = await evohomeControl(id, 'setpoint', value);
      if (seq !== setpointSeqRef.current) return;
      if (mode !== 'permanent_override') setLocalMode('temporary_override');
      if (res?.device) onUpdatedRef.current?.(res.device);
    } catch (e: any) {
      if (seq !== setpointSeqRef.current) return;
      setLocalSetpoint(null);
      const d = e?.response?.data?.detail;
      setError(typeof d === 'string' ? d : (d?.message || e?.message || t('evohome.modal.controlError', { defaultValue: 'Error de control' })));
    }
  }, [pauseModalSync, t, localMode, durationMin]);

  const scheduleSetpointSend = useCallback((next: number) => {
    pauseModalSync();
    pendingSetpointRef.current = next;
    if (setpointDebounceRef.current) clearTimeout(setpointDebounceRef.current);
    setpointDebounceRef.current = setTimeout(() => {
      setpointDebounceRef.current = null;
      const val = pendingSetpointRef.current;
      pendingSetpointRef.current = null;
      if (val != null) void flushSetpoint(val);
    }, SETPOINT_DEBOUNCE_MS);
  }, [flushSetpoint, pauseModalSync]);

  const flushPendingSetpointNow = useCallback(() => {
    if (setpointDebounceRef.current) {
      clearTimeout(setpointDebounceRef.current);
      setpointDebounceRef.current = null;
    }
    const val = pendingSetpointRef.current;
    pendingSetpointRef.current = null;
    if (val != null) void flushSetpoint(val);
  }, [flushSetpoint]);

  useEffect(() => {
    if (open) {
      setIsClosing(false);
      setError(null);
      setLocalSetpoint(null);
      setEditingSetpoint(false);
      setSetpointDraft('');
      setLocalMode(null);
      setConfigBusy(false);
      setCooldownUntil(0);
      setAmbientBusy(false);
      pendingSetpointRef.current = null;
      if (modeCooldownTimerRef.current) {
        clearTimeout(modeCooldownTimerRef.current);
        modeCooldownTimerRef.current = null;
      }
      if (setpointDebounceRef.current) {
        clearTimeout(setpointDebounceRef.current);
        setpointDebounceRef.current = null;
      }
    }
  }, [open, device?.id]);

  useEffect(() => {
    if (!open || !device?.id) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let first = true;

    const schedule = (ms: number) => {
      timer = setTimeout(() => { void tick(); }, ms);
    };

    const tick = async () => {
      if (cancelled) return;
      const pauseLeft = syncPausedUntilRef.current - Date.now();
      if (pauseLeft > 0) {
        schedule(pauseLeft);
        return;
      }
      let ok = false;
      try {
        const d = await evohomeRefreshDevice(device.id);
        if (!cancelled && d) onUpdatedRef.current?.(d);
        ok = true;
        if (first) {
          first = false;
          setError(null);
        }
      } catch (e: any) {
        if (!cancelled && first) {
          first = false;
          const detail = e?.response?.data?.detail;
          const msg = typeof detail === 'string' ? detail : detail?.message;
          if (msg) setError(msg);
        }
      }
      if (!cancelled) schedule(ok ? MODAL_SYNC_INTERVAL_MS : MODAL_SYNC_RETRY_MS);
    };

    void tick();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [open, device?.id]);

  useEffect(() => {
    if (localSetpoint == null) return;
    if (pendingSetpointRef.current != null) return;
    if (live?.setpoint != null && Math.abs(Number(live.setpoint) - localSetpoint) < 0.05) {
      setLocalSetpoint(null);
    }
  }, [live?.setpoint, localSetpoint]);

  useEffect(() => {
    if (localMode == null) return;
    if (normalizeMode(live?.setpoint_mode) === localMode
      || (localMode === 'clear_override' && normalizeMode(live?.setpoint_mode) === 'follow_schedule')) {
      setLocalMode(null);
    }
  }, [live?.setpoint_mode, localMode]);

  useEffect(() => () => {
    if (setpointDebounceRef.current) clearTimeout(setpointDebounceRef.current);
  }, []);

  const beginClose = useCallback(() => {
    flushPendingSetpointNow();
    setIsClosing(true);
    setTimeout(() => {
      onClose();
      setIsClosing(false);
    }, 280);
  }, [onClose, flushPendingSetpointNow]);

  const requestClose = useOverlayBackButton(open, beginClose, 'evohome-device-modal');
  const { noteActivity } = useCloudModalAutoClose(open, requestClose, live?.id ?? device?.id);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    const touch = e.touches[0];
    swipeStartRef.current = { x: touch.clientX, y: touch.clientY };
  }, []);

  const handleTouchEnd = useCallback((e: React.TouchEvent) => {
    if (!swipeStartRef.current) return;
    const touch = e.changedTouches[0];
    const deltaX = touch.clientX - swipeStartRef.current.x;
    const deltaY = Math.abs(touch.clientY - swipeStartRef.current.y);
    if (deltaX > 80 && deltaX > deltaY) requestClose();
    swipeStartRef.current = null;
  }, [requestClose]);

  useEffect(() => {
    if (!editingSetpoint) return undefined;
    const focusTimer = setTimeout(() => setpointInputRef.current?.focus(), 0);
    return () => clearTimeout(focusTimer);
  }, [editingSetpoint]);

  if (!open || !live) return null;

  const can = Boolean(live.can_control);
  const offline = !live.online;
  const modeUi = localMode ?? normalizeMode(live.setpoint_mode) ?? 'follow_schedule';
  const modeColor = MODE_META[modeUi]?.color || HEAT;
  const heatingLocked = isHeatingOff(live.system_mode);
  // Sin !can en disabled: el tap debe llegar a run() para mostrar el Snackbar de permisos.
  const setpointDisabled = offline || heatingLocked;
  const cmdCooling = nowMs < cooldownUntil;
  const modeDisabled = configBusy || cmdCooling || offline || heatingLocked;
  const denyControl = () => {
    setPermissionSnackbar({ open: true, message: t('permissions.noControlDevices') });
  };

  const armModeCooldown = () => {
    const until = Date.now() + EVOHOME_CMD_COOLDOWN_MS;
    setCooldownUntil(until);
    setNowMs(Date.now());
    setConfigBusy(true);
    if (modeCooldownTimerRef.current) clearTimeout(modeCooldownTimerRef.current);
    modeCooldownTimerRef.current = setTimeout(() => {
      modeCooldownTimerRef.current = null;
      setConfigBusy(false);
    }, EVOHOME_CMD_COOLDOWN_MS);
  };

  const displaySetpoint = localSetpoint != null
    ? localSetpoint
    : (live.setpoint != null ? Number(live.setpoint) : null);

  const refreshAmbient = async () => {
    if (ambientBusy) return;
    setError(null);
    setAmbientBusy(true);
    try {
      const d = await evohomeRefreshDevice(live.id, { force: true });
      if (d) onUpdated?.(d);
    } catch (e: any) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : detail?.message;
      setError(msg || e?.message || t('evohome.modal.ambientRefreshError', { defaultValue: 'No se pudo refrescar' }));
    } finally {
      setAmbientBusy(false);
    }
  };

  const executeRun = async (action: string, value?: unknown) => {
    setError(null);

    if (action === 'setpoint') {
      const next = Math.min(maxSp, Math.max(minSp, Number(value)));
      if (!Number.isFinite(next)) return;
      setLocalSetpoint(next);
      scheduleSetpointSend(next);
      return;
    }

    if (action === 'setpoint_mode' || action === 'clear_override') {
      if (configBusy || Date.now() < cooldownUntil) return;
      const mode = action === 'clear_override' ? 'clear_override' : String(value) as SetpointModeUi;
      // Re-tap del modo ya activo: no spamear TCC.
      if (mode !== 'clear_override' && mode === modeUi) return;
      setLocalMode(mode);
      pauseModalSync();
      armModeCooldown();
      try {
        let res;
        if (mode === 'clear_override' || mode === 'follow_schedule') {
          res = await evohomeControl(live.id, 'clear_override');
        } else if (mode === 'temporary_override') {
          const temp = displaySetpoint ?? live.setpoint ?? 20;
          res = await evohomeControl(live.id, 'setpoint_mode', {
            mode: 'temporary_override',
            temperature: temp,
            duration_minutes: durationMin,
          });
        } else {
          const temp = displaySetpoint ?? live.setpoint ?? 20;
          res = await evohomeControl(live.id, 'setpoint_mode', {
            mode: 'permanent_override',
            temperature: temp,
          });
        }
        if (res?.device) onUpdated?.(res.device);
      } catch (e: any) {
        setLocalMode(null);
        const d = e?.response?.data?.detail;
        setError(typeof d === 'string' ? d : (d?.message || e?.message || t('evohome.modal.controlError', { defaultValue: 'Error de control' })));
      }
      return;
    }

    try {
      const res = await evohomeControl(live.id, action, value);
      if (res?.device) onUpdated?.(res.device);
    } catch (e: any) {
      const d = e?.response?.data?.detail;
      setError(typeof d === 'string' ? d : (d?.message || e?.message || t('evohome.modal.controlError', { defaultValue: 'Error de control' })));
    }
  };

  const run = async (action: string, value?: unknown) => {
    if (!can) {
      denyControl();
      return;
    }
    if (offline) {
      setError(t('evohome.modal.offlineError', { defaultValue: 'Zona offline' }));
      return;
    }
    if (!requirePinIfNeeded(live.pin_required, live.id, () => {
      void executeRun(action, value);
    })) return;
    await executeRun(action, value);
  };

  const beginEditSetpoint = () => {
    if (!can) {
      denyControl();
      return;
    }
    if (setpointDisabled) return;
    setSetpointDraft(displaySetpoint != null ? String(displaySetpoint) : '');
    setEditingSetpoint(true);
  };

  const commitEditSetpoint = () => {
    if (!editingSetpoint) return;
    setEditingSetpoint(false);
    const raw = setpointDraft.replace(/[^\d.]/g, '');
    if (raw === '') return;
    const n = Math.min(maxSp, Math.max(minSp, parseFloat(raw)));
    if (!Number.isFinite(n)) return;
    if (displaySetpoint != null && Math.abs(n - displaySetpoint) < 0.05) return;
    void run('setpoint', n);
  };

  const cancelEditSetpoint = () => {
    setEditingSetpoint(false);
    setSetpointDraft('');
  };

  const statusColor = offline ? '#e57373' : '#66bb6a';
  const statusLabel = offline ? t('devices.offline') : t('devices.online');
  const step = 0.5;
  const untilLabel = live.until_ts
    ? formatInServerTz(new Date(live.until_ts), 'dd/MM HH:mm')
    : null;

  const header = (
    <Box sx={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      px: 1,
      py: 0.65,
      borderBottom: `1px solid ${SURFACE_BORDER}`,
      background: 'linear-gradient(180deg, #1c1c1c 0%, #141414 100%)',
      minHeight: 40,
      flexShrink: 0,
      gap: 0.5,
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0, flexShrink: 1 }}>
        <StatusChip color={statusColor} label={statusLabel} />
      </Box>
      <Box sx={{ flex: 1, textAlign: 'center', px: 0.5, minWidth: 0 }}>
        <Typography sx={{
          color: 'white',
          fontWeight: 700,
          fontSize: '0.82rem',
          lineHeight: 1.15,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}>
          {live.nombre}
        </Typography>
        <Typography sx={{
          fontSize: 10,
          color: 'rgba(255,255,255,0.4)',
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          mt: 0.15,
        }}>
          {t('evohome.modal.deviceKind', { defaultValue: 'Zona · EvoHome' })}
        </Typography>
      </Box>
      <IconButton onClick={requestClose} sx={{
        color: 'white',
        p: 0.55,
        flexShrink: 0,
        transition: PRESS_TRANSITION,
        '&:active': { transform: 'scale(0.9)' },
      }}>
        <CloseIcon sx={{ fontSize: 18 }} />
      </IconButton>
    </Box>
  );

  const body = (
    <Box sx={{
      flex: 1,
      minHeight: 0,
      overflowY: 'auto',
      px: 1.75,
      py: 1.1,
      display: 'flex',
      flexDirection: 'column',
      gap: 1.25,
      background: 'radial-gradient(120% 60% at 50% -10%, rgba(35,145,255,0.08) 0%, transparent 50%)',
    }}>
      <Box sx={{
        position: 'relative',
        borderRadius: '18px',
        px: 1.65,
        pt: 1.2,
        pb: 1.25,
        border: `1px solid ${SURFACE_BORDER}`,
        background: `
          radial-gradient(80% 70% at 50% 0%, ${modeColor}26 0%, transparent 60%),
          linear-gradient(180deg, rgba(255,255,255,0.04) 0%, rgba(0,0,0,0.2) 100%)
        `,
      }}>
        <Box sx={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-evenly',
          gap: 0.85,
        }}>
          <Box
            role="button"
            tabIndex={0}
            aria-label={t('evohome.modal.refreshAmbient', { defaultValue: 'Refrescar ambiente' })}
            onClick={() => { void refreshAmbient(); }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                void refreshAmbient();
              }
            }}
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              minWidth: 0,
              cursor: ambientBusy ? 'wait' : 'pointer',
              borderRadius: '12px',
              px: 0.4,
              WebkitTapHighlightColor: 'transparent',
              transition: 'opacity 180ms ease, transform 120ms ease',
              opacity: ambientBusy ? 0.55 : 1,
              '&:active': ambientBusy ? undefined : { transform: 'scale(0.97)' },
            }}
          >
            <Typography sx={{
              fontSize: 11,
              color: 'rgba(255,255,255,0.45)',
              fontWeight: 600,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              mb: 0.3,
              lineHeight: 1.15,
              textAlign: 'center',
            }}>
              {ambientBusy
                ? t('evohome.modal.ambientBusy', { defaultValue: 'Leyendo…' })
                : t('samsung.modal.ambient')}
            </Typography>
            <Typography sx={{
              fontSize: 33,
              fontWeight: 720,
              lineHeight: 1.12,
              letterSpacing: '-0.03em',
              color: '#fff',
              textShadow: `0 0 28px ${modeColor}55`,
            }}>
              {live.temperature != null ? `${Number(live.temperature).toFixed(1)}°` : '—'}
            </Typography>
          </Box>

          <Box sx={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 0.55,
            minWidth: 0,
          }}>
            <RoundControl
              ariaLabel={t('evohome.modal.setpointDown', { defaultValue: 'Bajar setpoint' })}
              disabled={setpointDisabled}
              onClick={() => {
                if (editingSetpoint) cancelEditSetpoint();
                void run('setpoint', (displaySetpoint ?? 20) - step);
              }}
              size={37}
            >
              <RemoveIcon sx={{ fontSize: 22 }} />
            </RoundControl>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 53 }}>
              <Typography sx={{
                fontSize: 11,
                color: 'rgba(255,255,255,0.45)',
                fontWeight: 600,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                mb: 0.3,
                lineHeight: 1.15,
                textAlign: 'center',
              }}>
                {t('samsung.modal.target')}
              </Typography>
              {editingSetpoint ? (
                <TextField
                  inputRef={setpointInputRef}
                  value={setpointDraft}
                  onChange={(e) => {
                    const v = e.target.value.replace(/[^\d.]/g, '').slice(0, 5);
                    setSetpointDraft(v);
                  }}
                  onBlur={() => { commitEditSetpoint(); }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      (e.target as HTMLInputElement).blur();
                    } else if (e.key === 'Escape') {
                      e.preventDefault();
                      cancelEditSetpoint();
                    }
                  }}
                  variant="standard"
                  inputProps={{
                    inputMode: 'decimal',
                    style: {
                      textAlign: 'center',
                      fontSize: 31,
                      fontWeight: 750,
                      letterSpacing: '-0.02em',
                      color: modeColor,
                      padding: 0,
                      width: 56,
                    },
                  }}
                  sx={{
                    width: 56,
                    '& .MuiInput-underline:before': { borderBottomColor: `${modeColor}66` },
                    '& .MuiInput-underline:after': { borderBottomColor: modeColor },
                  }}
                />
              ) : (
                <Typography
                  role="button"
                  tabIndex={setpointDisabled ? -1 : 0}
                  onClick={() => { beginEditSetpoint(); }}
                  sx={{
                    textAlign: 'center',
                    fontSize: 31,
                    fontWeight: 750,
                    letterSpacing: '-0.02em',
                    lineHeight: 1.12,
                    color: modeColor,
                    cursor: setpointDisabled ? 'default' : 'pointer',
                  }}
                >
                  {displaySetpoint != null
                    ? `${Number.isInteger(displaySetpoint) ? displaySetpoint : displaySetpoint.toFixed(1)}°`
                    : '—'}
                </Typography>
              )}
            </Box>
            <RoundControl
              ariaLabel={t('evohome.modal.setpointUp', { defaultValue: 'Subir setpoint' })}
              disabled={setpointDisabled}
              onClick={() => {
                if (editingSetpoint) cancelEditSetpoint();
                void run('setpoint', (displaySetpoint ?? 20) + step);
              }}
              size={37}
            >
              <AddIcon sx={{ fontSize: 22 }} />
            </RoundControl>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 0.75 }}>
          <WhatshotIcon sx={{ fontSize: 18, color: HEAT, opacity: live.asking_heat ? 1 : 0.28 }} />
        </Box>
      </Box>

      <Box>
        {heatingLocked && (
          <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#EF5350', lineHeight: 1.35, mb: 0.85 }}>
            {t('evohome.heatingLocked')}
          </Typography>
        )}
        <SectionLabel>{t('evohome.modal.mode', { defaultValue: 'Modo' })}</SectionLabel>
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
          gap: 0.65,
        }}>
          {(['follow_schedule', 'temporary_override', 'permanent_override', 'clear_override'] as SetpointModeUi[]).map((m) => {
            const meta = MODE_META[m];
            const selected = m === 'clear_override'
              ? false
              : modeUi === m;
            return (
              <ChoiceChip
                key={m}
                selected={selected}
                disabled={modeDisabled}
                color={meta.color}
                onClick={() => {
                  if (m === 'clear_override') void run('clear_override');
                  else void run('setpoint_mode', m);
                }}
                minHeight={58}
              >
                <Box sx={{ color: selected ? meta.color : 'rgba(255,255,255,0.55)', display: 'flex' }}>
                  {meta.icon}
                </Box>
                <Typography sx={{ fontSize: 9.5, fontWeight: 700, lineHeight: 1.1, textAlign: 'center' }}>
                  {t(`evohome.mode.${m}`, {
                    defaultValue: m === 'follow_schedule' ? 'Programa'
                      : m === 'temporary_override' ? 'Temporal'
                        : m === 'permanent_override' ? 'Permanente'
                          : 'Cancelar',
                  })}
                </Typography>
              </ChoiceChip>
            );
          })}
        </Box>
      </Box>

      <Box>
        <SectionLabel>
          {t('evohome.modal.duration', { defaultValue: 'Duración / hasta' })}
        </SectionLabel>
        <Box sx={{
          display: 'flex',
          gap: 0.5,
          flexWrap: 'wrap',
          mb: untilLabel ? 0.75 : 0,
        }}>
          {DURATION_OPTIONS_MIN.map((mins) => {
            const selected = durationMin === mins;
            return (
              <ChoiceChip
                key={mins}
                selected={selected}
                disabled={modeDisabled}
                color="#FFB74D"
                onClick={() => {
                  if (!can) {
                    denyControl();
                    return;
                  }
                  setDurationMin(mins);
                }}
                minHeight={36}
              >
                <Typography sx={{ fontSize: 11, fontWeight: 700 }}>
                  {mins < 60 ? `${mins}m` : `${mins / 60}h`}
                </Typography>
              </ChoiceChip>
            );
          })}
        </Box>
        {untilLabel && (
          <Typography sx={{ fontSize: 11, color: 'rgba(255,255,255,0.55)' }}>
            {t('evohome.modal.until', { defaultValue: 'Hasta: {{when}}', when: untilLabel })}
          </Typography>
        )}
      </Box>

      <Box sx={{ mt: (theme) => `calc(${theme.spacing(0.75)} + 15px)` }}>
        <EvohomeTempChart deviceId={live.id} />
      </Box>

      {error && (
        <Box sx={{
          px: 1.25,
          py: 0.85,
          borderRadius: '12px',
          bgcolor: 'rgba(229,115,115,0.12)',
          border: '1px solid rgba(229,115,115,0.35)',
        }}>
          <Typography sx={{ color: '#ef9a9a', fontSize: 12, textAlign: 'center', lineHeight: 1.35 }}>
            {error}
          </Typography>
        </Box>
      )}
    </Box>
  );

  const permissionSnack = (
    <Snackbar
      open={permissionSnackbar.open}
      autoHideDuration={4000}
      onClose={() => setPermissionSnackbar({ ...permissionSnackbar, open: false })}
      {...getDemoRestrictionSnackbarProps()}
    >
      <Alert
        onClose={() => setPermissionSnackbar({ ...permissionSnackbar, open: false })}
        severity="error"
        sx={{ width: '100%' }}
      >
        {permissionSnackbar.message}
      </Alert>
    </Snackbar>
  );

  if (isHorizontal) {
    return (
      <Box
        onPointerDownCapture={noteActivity}
        onWheelCapture={noteActivity}
        onKeyDownCapture={noteActivity}
        onTouchStart={(e) => { noteActivity(); handleTouchStart(e); }}
        onTouchEnd={handleTouchEnd}
        sx={{ width: '100%', height: '100%', backgroundColor: '#000', color: 'white', display: 'flex', flexDirection: 'column' }}
      >
        <Slide direction="left" in={open && !isClosing} timeout={300} appear mountOnEnter unmountOnExit>
          <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Paper sx={{
              width: '100%', height: '100%', backgroundColor: '#000', color: 'white',
              display: 'flex', flexDirection: 'column', borderRadius: 0, boxShadow: 'none',
            }}>
              {header}
              {body}
            </Paper>
          </Box>
        </Slide>
        {permissionSnack}
      </Box>
    );
  }

  return (
    <Box
      onPointerDownCapture={noteActivity}
      onWheelCapture={noteActivity}
      onKeyDownCapture={noteActivity}
      onTouchStart={(e) => { noteActivity(); handleTouchStart(e); }}
      sx={{
        position: 'fixed', inset: 0, zIndex: 1360,
        display: open ? 'flex' : 'none',
        alignItems: 'center', justifyContent: 'center',
      }}
    >
      <Box onClick={requestClose} sx={{ position: 'absolute', inset: 0, backgroundColor: 'transparent' }} />
      <Slide direction="left" in={open && !isClosing} timeout={300} appear mountOnEnter unmountOnExit>
        <Paper
          onTouchEnd={handleTouchEnd}
          sx={{
            position: 'fixed', bottom: SHC_BOTTOM_NAV_CLEARANCE, top: '40px', left: '1%', right: '1%', width: '98%',
            backgroundColor: '#000', color: 'white', zIndex: 1400,
            display: 'flex', flexDirection: 'column', overflow: 'hidden',
            borderRadius: '16px 16px 0 0',
            boxShadow: '-4px 0 20px rgba(0,0,0,0.5)',
            border: '1px solid #444', borderTop: '2px solid #2391FF',
          }}
        >
          {header}
          {body}
        </Paper>
      </Slide>
      {permissionSnack}
    </Box>
  );
};

export default EvohomeDeviceModal;
